/**
 * Graphics device port — apps talk to GraphicsApi, never import three directly.
 * Drivers: "three" (default WebGL), "canvas2d" (lightweight / degraded).
 */
import * as THREE from "three";
import { GLSL_CHUNKS, resolveGlslChunks, type GlslChunkName } from "./glsl-chunks";
import {
  createSurface2D,
  type Surface2DOptions,
  type Surface2DHandle,
} from "./surface2d";

import {
  exportMesh,
  type MeshPiece,
  type MeshExportRequest,
  type MeshExportResult,
} from "./mesh-export";

export type {
  Surface2DOptions,
  Surface2DHandle,
  Surface2DInput,
  Surface2DPointer,
  Surface2DScaling,
} from "./surface2d";

export type {
  MeshPiece,
  MeshGroup,
  MeshExportOptions,
  MeshExportInfo,
  MeshExportFormat,
  MeshExportFile,
  MeshExportRequest,
  MeshExportResult,
} from "./mesh-export";

export type GfxSurface = {
  id: string;
  canvas: HTMLCanvasElement;
  /** Present on three backend */
  renderer?: THREE.WebGLRenderer;
  scene?: THREE.Scene;
  camera?: THREE.Camera;
  /** Present on canvas2d backend */
  ctx2d?: CanvasRenderingContext2D | null;
  resize: () => void;
  dispose: () => void;
  setOrtho2D: () => void;
  setPerspective: (fov?: number, near?: number, far?: number) => void;
};

/**
 * Options for ctx.graphics.loadTexture. Defaults are tuned for a HEIGHTMAP,
 * because that is the case a wrong default silently ruins.
 */
export type LoadTextureOpts = {
  /** Edge behaviour. Default "clamp" — a tiled heightfield seams visibly. */
  wrap?: "clamp" | "repeat" | "mirror";
  /** Sampling filter. Default "linear". "nearest" to read exact texels. */
  filter?: "linear" | "nearest";
  /**
   * Build a mip chain. Default FALSE. Mipmapping a heightfield averages peaks
   * with valleys, so the terrain silhouette softens and flattens with distance
   * — the classic "why do my far mountains look like hills" bug. Turn it on
   * only for colour/detail maps that are minified.
   */
  mipmaps?: boolean;
  /**
   * Flip on load. Default TRUE, matching three's ImageLoader convention and
   * what every existing wallpaper's `vUv` expects. Set false if your bake
   * already wrote rows bottom-up.
   */
  flipY?: boolean;
  /**
   * Treat texel values as colour and sRGB-decode them. Default FALSE.
   * A height texture MUST NOT be decoded: sRGB is a non-linear curve, so every
   * elevation would come back wrong (and non-uniformly wrong, which reads as a
   * plausible-but-incorrect terrain rather than an obvious bug).
   */
  srgb?: boolean;
  /** Anisotropic samples, clamped to the device max. Default 1. */
  anisotropy?: number;
};

/**
 * A shader-quad uniform: scalar, vec2/vec3/vec4, or a texture (sampler2D).
 *
 * Textures are passed through to three UNWRAPPED — three's WebGLUniforms binds
 * a Texture value to a sampler and allocates the texture unit itself. Wrapping
 * one in a Vector, as the array branch does for numbers, would break that.
 */
export type ShaderQuadUniformValue = number | number[] | THREE.Texture;

export type ShaderQuadOpts = {
  /**
   * Fragment shader body. A prelude declaring `precision highp float`, `vUv`,
   * `uTime`, `uRes` and `uPaused` is prepended — write helpers + `void main()`.
   */
  fragment: string;
  /**
   * Shared GLSL helpers to prepend after the prelude: "hash", "noise", "fbm",
   * "stars". Dependencies come along automatically ("fbm" pulls "noise" and
   * "hash"), so a wallpaper never re-declares them. See ctx.graphics.glsl.
   */
  chunks?: readonly (GlslChunkName | string)[];
  /**
   * Extra uniforms; scalars, 2/3/4-length arrays and textures are supported.
   * Declare a texture in your fragment as `uniform sampler2D uName;`.
   */
  uniforms?: Record<string, ShaderQuadUniformValue>;
  /**
   * Textures the quad OWNS: disposed with it, alongside material and geometry.
   * `ctx.graphics.loadTexture` hands back a texture nobody else is tracking, so
   * without this every wallpaper switch leaks a GPU allocation. Textures passed
   * only through `uniforms` are NOT auto-disposed — the quad cannot know
   * whether you share them between quads.
   */
  ownedTextures?: readonly THREE.Texture[];
  /** Opaque background behind the quad. Default 0x07080f. */
  clearColor?: number;
  /** uTime multiplier. Default 1. */
  speed?: number;
  /**
   * Render below display resolution and let the browser upscale. 1 = native
   * (clamped to DPR 2). Use ~0.6–0.75 for heavy per-pixel work like a
   * raymarcher — a soft wallpaper is far better than a stuttering desktop.
   */
  resolutionScale?: number;
  /** Freeze uTime under prefers-reduced-motion. Default true. */
  respectReducedMotion?: boolean;
  /** CSS background used when WebGL is unavailable (canvas2d backend). */
  fallbackCss?: string;
  /** Called with the GL compile/link log if the shader fails. */
  onError?: (message: string) => void;
};

export type ShaderQuadHandle = {
  /** "three" when the shader really runs; "canvas2d" when the CSS fallback painted. */
  backend: "three" | "canvas2d";
  canvas: HTMLCanvasElement | null;
  /** Freeze/resume uTime. The last frame stays on screen while paused. */
  setPaused: (paused: boolean) => void;
  setUniform: (name: string, value: ShaderQuadUniformValue) => void;
  setSpeed: (speed: number) => void;
  dispose: () => void;
};

export type GraphicsApi = {
  /** Active driver id */
  backend: "three" | "canvas2d" | string;
  /** THREE namespace when backend is three; null otherwise */
  three: typeof THREE | null;
  /**
   * Source of the shared GLSL chunks, for shaders that would rather paste them
   * by hand than use `chunks`. Same text the host prepends.
   */
  glsl: Readonly<Record<GlslChunkName, string>>;
  createSurface: (
    parent: HTMLElement,
    opts?: { id?: string; alpha?: boolean; autoRender?: boolean }
  ) => GfxSurface;
  createShaderQuad: (parent: HTMLElement, opts: ShaderQuadOpts) => ShaderQuadHandle;
  /**
   * Fixed-resolution 2D canvas surface — the canvas2d twin of createShaderQuad.
   * The host owns the letterbox, the frame loop, focus-scoped input and
   * disposal. Unlike createSurface it does NOT apply devicePixelRatio, which is
   * what keeps pixel art sharp. See surface2d.ts.
   */
  create2D: (parent: HTMLElement, opts: Surface2DOptions) => Surface2DHandle;
  /**
   * Write geometry out as GLB or OBJ. Input is a plain data shape, not a THREE
   * scene — see mesh-export/.
   */
  exportMesh: (
    pieces: readonly MeshPiece[],
    request: MeshExportRequest
  ) => MeshExportResult;
  /**
   * Load an image as a GPU texture. Returns the texture immediately (usable as
   * a uniform at once, sampling black until the image lands) plus a `ready`
   * promise. Defaults suit a heightmap: clamp, linear, NO mipmaps, NO sRGB.
   * Pass it in `ownedTextures` so the quad disposes it.
   */
  loadTexture: (
    url: string,
    opts?: LoadTextureOpts
  ) => { texture: THREE.Texture; ready: Promise<THREE.Texture> };
  getSurface: (id: string) => GfxSurface | null;
  disposeSurface: (id: string) => void;
};

type SurfaceRec = GfxSurface & {
  raf?: number;
  autoRender: boolean;
  ro: ResizeObserver;
};

const surfaces = new Map<string, SurfaceRec>();

function uid(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function createThreeSurface(
  parent: HTMLElement,
  opts?: { id?: string; alpha?: boolean; autoRender?: boolean }
): GfxSurface {
  const id = opts?.id || uid("surf");
  if (surfaces.has(id)) throw new Error(`graphics surface already exists: ${id}`);

  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "display:block;width:100%;height:100%;touch-action:none;position:absolute;inset:0;z-index:0";
  if (getComputedStyle(parent).position === "static") {
    parent.style.position = "relative";
  }
  parent.appendChild(canvas);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: opts?.alpha ?? true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  if (opts?.alpha !== false) renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  let camera: THREE.Camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 10);
  camera.position.z = 1;

  const resize = () => {
    const w = Math.max(1, parent.clientWidth || 1);
    const h = Math.max(1, parent.clientHeight || 1);
    renderer.setSize(w, h, false);
    if (camera instanceof THREE.OrthographicCamera) {
      const aspect = w / h;
      camera.left = -aspect;
      camera.right = aspect;
      camera.top = 1;
      camera.bottom = -1;
      camera.updateProjectionMatrix();
    } else if (camera instanceof THREE.PerspectiveCamera) {
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
  };

  const setOrtho2D = () => {
    const w = Math.max(1, parent.clientWidth || 1);
    const h = Math.max(1, parent.clientHeight || 1);
    const aspect = w / h;
    const cam = new THREE.OrthographicCamera(-aspect, aspect, 1, -1, 0, 10);
    cam.position.z = 1;
    camera = cam;
    rec.camera = cam;
    resize();
  };

  const setPerspective = (fov = 50, near = 0.1, far = 1000) => {
    const w = Math.max(1, parent.clientWidth || 1);
    const h = Math.max(1, parent.clientHeight || 1);
    const cam = new THREE.PerspectiveCamera(fov, w / h, near, far);
    cam.position.set(0, 0, 5);
    camera = cam;
    rec.camera = cam;
    resize();
  };

  const ro = new ResizeObserver(() => resize());
  ro.observe(parent);

  const dispose = () => {
    if (rec.raf) cancelAnimationFrame(rec.raf);
    ro.disconnect();
    scene.traverse((obj) => {
      const m = obj as THREE.Mesh;
      if (m.geometry) m.geometry.dispose?.();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose?.());
      else mat?.dispose?.();
    });
    renderer.dispose();
    canvas.remove();
    surfaces.delete(id);
  };

  const rec: SurfaceRec = {
    id,
    canvas,
    renderer,
    scene,
    camera,
    resize,
    dispose,
    setOrtho2D,
    setPerspective,
    autoRender: opts?.autoRender !== false,
    ro,
  };

  surfaces.set(id, rec);
  resize();

  if (rec.autoRender) {
    const tick = () => {
      rec.raf = requestAnimationFrame(tick);
      if (document.hidden) return;
      renderer.render(scene, rec.camera as THREE.Camera);
    };
    tick();
  }

  return rec;
}

function createCanvas2dSurface(
  parent: HTMLElement,
  opts?: { id?: string; alpha?: boolean; autoRender?: boolean }
): GfxSurface {
  const id = opts?.id || uid("surf2d");
  if (surfaces.has(id)) throw new Error(`graphics surface already exists: ${id}`);

  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "display:block;width:100%;height:100%;touch-action:none;position:absolute;inset:0;z-index:0";
  if (getComputedStyle(parent).position === "static") {
    parent.style.position = "relative";
  }
  parent.appendChild(canvas);
  const ctx2d = canvas.getContext("2d");

  const resize = () => {
    const w = Math.max(1, parent.clientWidth || 1);
    const h = Math.max(1, parent.clientHeight || 1);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    if (ctx2d) {
      ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (opts?.alpha === false) {
        ctx2d.fillStyle = "#07080f";
        ctx2d.fillRect(0, 0, w, h);
      } else {
        ctx2d.clearRect(0, 0, w, h);
      }
    }
  };

  const ro = new ResizeObserver(() => resize());
  ro.observe(parent);

  const dispose = () => {
    if (rec.raf) cancelAnimationFrame(rec.raf);
    ro.disconnect();
    canvas.remove();
    surfaces.delete(id);
  };

  const rec: SurfaceRec = {
    id,
    canvas,
    ctx2d,
    resize,
    dispose,
    setOrtho2D: () => {},
    setPerspective: () => {},
    autoRender: false,
    ro,
  };
  surfaces.set(id, rec);
  resize();
  return rec;
}

const SHADER_QUAD_PRELUDE = `precision highp float;
varying vec2 vUv;
uniform float uTime;
uniform vec2 uRes;
uniform float uPaused;
`;

const SHADER_QUAD_VERTEX = `varying vec2 vUv;
void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

function toUniform(value: ShaderQuadUniformValue): { value: unknown } {
  // Textures go through raw. three's WebGLUniforms detects a Texture, binds it
  // to a sampler and allocates the texture unit; wrapping it the way the array
  // branch wraps numbers would hand the sampler a Vector and compile to black.
  if (value instanceof THREE.Texture) return { value };
  if (Array.isArray(value)) {
    if (value.length === 2) return { value: new THREE.Vector2(value[0], value[1]) };
    if (value.length === 3) return { value: new THREE.Vector3(value[0], value[1], value[2]) };
    if (value.length === 4) {
      return { value: new THREE.Vector4(value[0], value[1], value[2], value[3]) };
    }
    return { value: value.slice() };
  }
  return { value };
}

function assignUniform(target: { value: unknown }, value: ShaderQuadUniformValue) {
  // Swap the reference, never mutate in place: a Texture has no numeric set().
  // Disposing the OLD texture is the caller's business — it may be shared.
  if (value instanceof THREE.Texture) {
    target.value = value;
    return;
  }
  const cur = target.value as { set?: (...n: number[]) => void };
  if (Array.isArray(value) && typeof cur?.set === "function") cur.set(...value);
  else target.value = Array.isArray(value) ? value.slice() : value;
}

/**
 * Load an image as a GPU texture with defaults that suit a HEIGHTMAP.
 *
 * Returns the Texture synchronously plus a `ready` promise, rather than a
 * promise of the texture. Two reasons:
 *  - the texture object is valid the instant it exists, so it can go straight
 *    into `uniforms` at createShaderQuad time. The quad renders (sampling
 *    black) and self-corrects the frame the image lands — no await, no
 *    two-phase mount, no "build the quad in a .then()" dance that every caller
 *    would otherwise repeat;
 *  - a module that unmounts mid-load still holds a real object to dispose. With
 *    a bare promise there is nothing to dispose until it resolves, which is
 *    exactly when a wallpaper switch leaks.
 * Await `ready` when you genuinely need the pixels (e.g. to read dimensions).
 */
function loadTextureImpl(url: string, opts?: LoadTextureOpts) {
  const wrapMode =
    opts?.wrap === "repeat"
      ? THREE.RepeatWrapping
      : opts?.wrap === "mirror"
        ? THREE.MirroredRepeatWrapping
        : THREE.ClampToEdgeWrapping;
  const mip = opts?.mipmaps === true;
  const nearest = opts?.filter === "nearest";
  const mag = nearest ? THREE.NearestFilter : THREE.LinearFilter;
  // With no mip chain the min filter MUST be a non-mipmap filter, or the
  // texture samples as incomplete and reads pure black on some drivers.
  const min = mip
    ? nearest
      ? THREE.NearestMipmapNearestFilter
      : THREE.LinearMipmapLinearFilter
    : mag;

  const texture = new THREE.Texture();
  const apply = () => {
    texture.wrapS = wrapMode;
    texture.wrapT = wrapMode;
    texture.magFilter = mag;
    texture.minFilter = min;
    texture.generateMipmaps = mip;
    texture.flipY = opts?.flipY ?? true;
    // NoColorSpace = the sampler returns the bytes that were baked. SRGBColorSpace
    // applies a ~2.2 decode, which is right for albedo and catastrophic for a
    // height, a normal, a mask or a packed 16-bit value.
    texture.colorSpace = opts?.srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    texture.anisotropy = Math.max(1, Math.floor(opts?.anisotropy ?? 1));
    texture.needsUpdate = true;
  };
  apply();

  const ready = new Promise<THREE.Texture>((resolve, reject) => {
    const img = new Image();
    // Module assets are same-origin (/modules/…), but a wallpaper pointing at a
    // remote URL would otherwise taint and fail; anonymous CORS is the safe ask.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      texture.image = img;
      apply();
      resolve(texture);
    };
    img.onerror = () => reject(new Error(`failed to load texture: ${url}`));
    img.src = url;
  });

  return { texture, ready };
}

/** Idempotent, throw-proof: dispose() runs on teardown paths that must not fail. */
function disposeOwned(textures: readonly THREE.Texture[] | undefined) {
  for (const t of textures || []) {
    try {
      t.dispose();
    } catch {
      /* already disposed, or a stub in a test environment */
    }
  }
}

/**
 * Every quad owns a WebGL context and browsers cap how many may be live at once
 * (commonly 8–16) — past the cap the *oldest* context is dropped, which is why
 * `webglcontextlost` has to degrade to `fallbackCss` rather than leave a black
 * canvas. Counted here so the budget is visible before it bites.
 */
let liveShaderQuads = 0;
let warnedQuadBudget = false;
const SHADER_QUAD_BUDGET = 8;

/**
 * Full-bleed fragment-shader surface — the canonical way to write an animated
 * wallpaper, and equally the way to paint any element (a widget tile, a panel)
 * with a shader. Owns the renderer, the fullscreen quad, resize, DPR clamping,
 * reduced-motion / tab-hidden / offscreen pausing, WebGL context-loss fallback
 * and disposal, so a caller is little more than its shader.
 */
function createShaderQuadImpl(
  parent: HTMLElement,
  opts: ShaderQuadOpts,
  backend: "three" | "canvas2d"
): ShaderQuadHandle {
  const paintFallback = (): ShaderQuadHandle => {
    // Must paint something: WallpaperHost treats an unpainted host as "module
    // declined" and swaps in the builtin aurora.
    parent.style.background = opts.fallbackCss || "#07080f";
    return {
      backend: "canvas2d",
      canvas: null,
      setPaused: () => {},
      setUniform: () => {},
      setSpeed: () => {},
      dispose: () => {
        parent.style.background = "";
        // The CSS fallback never uploaded these, but the caller still handed
        // ownership over and will not dispose them itself. Skipping this makes
        // the canvas2d driver — and every WebGL-init failure — the one path
        // that leaks, which is the path hardest to notice.
        disposeOwned(opts.ownedTextures);
      },
    };
  };

  if (backend !== "three") return paintFallback();

  let renderer: THREE.WebGLRenderer;
  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "display:block;width:100%;height:100%;position:absolute;inset:0;z-index:0";
  if (getComputedStyle(parent).position === "static") parent.style.position = "relative";
  parent.appendChild(canvas);

  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
  } catch (e) {
    canvas.remove();
    opts.onError?.(String((e as Error)?.message || e));
    return paintFallback();
  }
  liveShaderQuads++;
  if (liveShaderQuads > SHADER_QUAD_BUDGET && !warnedQuadBudget) {
    warnedQuadBudget = true;
    console.warn(
      `[gfx] ${liveShaderQuads} live shader quads — browsers cap WebGL contexts ` +
        `(~8-16) and drop the OLDEST when the cap is hit. Quads past the cap fall ` +
        `back to their fallbackCss. Use shader tiles sparingly and dispose them.`
    );
  }

  const scale = Math.min(1, Math.max(0.25, opts.resolutionScale ?? 1));
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2) * scale);
  renderer.setClearColor(opts.clearColor ?? 0x07080f, 1);

  const w0 = Math.max(1, parent.clientWidth || 1);
  const h0 = Math.max(1, parent.clientHeight || 1);
  const uniforms: Record<string, { value: unknown }> = {
    uTime: { value: 0 },
    uRes: { value: new THREE.Vector2(w0, h0) },
    uPaused: { value: 0 },
  };
  for (const [k, v] of Object.entries(opts.uniforms || {})) uniforms[k] = toUniform(v);

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: SHADER_QUAD_VERTEX,
    fragmentShader: SHADER_QUAD_PRELUDE + resolveGlslChunks(opts.chunks) + opts.fragment,
    depthTest: false,
    depthWrite: false,
  });

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  quad.frustumCulled = false;
  scene.add(quad);

  // Surface a bad shader instead of silently rendering black.
  if (opts.onError) {
    const onShaderError = (
      _gl: WebGLRenderingContext,
      _program: WebGLProgram,
      vs: WebGLShader,
      fs: WebGLShader
    ) => {
      const gl = renderer.getContext();
      const log = [gl.getShaderInfoLog(vs), gl.getShaderInfoLog(fs)]
        .filter(Boolean)
        .join("\n");
      opts.onError?.(log || "shader failed to compile");
    };
    (renderer.debug as { onShaderError?: unknown }).onShaderError = onShaderError;
  }

  const reduced =
    opts.respectReducedMotion !== false &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let speed = opts.speed ?? 1;
  let paused = false;
  let time = 0;
  let last = performance.now();
  let raf = 0;
  let disposed = false;
  // A tile scrolled away, covered, or on a hidden surface must not burn a frame
  // budget. No IntersectionObserver (jsdom, ancient browsers) → assume visible.
  let visible = true;
  let contextLost = false;
  let fallbackPainted = false;

  const frozen = () => paused || reduced || !visible || document.hidden;

  const io =
    typeof IntersectionObserver === "function"
      ? new IntersectionObserver(
          (entries) => {
            const e = entries[entries.length - 1];
            if (!e) return;
            const now = e.isIntersecting && e.intersectionRatio > 0;
            if (now === visible) return;
            visible = now;
            last = performance.now(); // don't accumulate the offscreen gap
          },
          { threshold: 0 }
        )
      : null;
  io?.observe(parent);

  const resize = () => {
    const w = Math.max(1, parent.clientWidth || 1);
    const h = Math.max(1, parent.clientHeight || 1);
    renderer.setSize(w, h, false);
    (uniforms.uRes.value as THREE.Vector2).set(w, h);
  };
  const ro = new ResizeObserver(resize);
  ro.observe(parent);
  window.addEventListener("resize", resize);
  resize();

  const tick = () => {
    if (disposed || contextLost) return;
    raf = requestAnimationFrame(tick);
    const now = performance.now();
    const dt = Math.min(0.1, (now - last) / 1000); // clamp: tab-restore shouldn't jump
    last = now;
    if (!frozen()) {
      time += dt * speed;
      uniforms.uTime.value = time;
    }
    uniforms.uPaused.value = frozen() ? 1 : 0;
    // Offscreen or hidden: keep the loop alive (cheap) but skip the GPU work.
    if (!document.hidden && visible) renderer.render(scene, camera);
  };
  tick();

  const onVisibility = () => {
    last = performance.now();
  };
  document.addEventListener("visibilitychange", onVisibility);

  // The browser dropped our context (usually because another quad pushed past
  // the cap). Without preventDefault it can never be restored; without the CSS
  // fallback the caller is left with a silently black canvas.
  const onContextLost = (e: Event) => {
    e.preventDefault();
    contextLost = true;
    cancelAnimationFrame(raf);
    raf = 0;
    parent.style.background = opts.fallbackCss || "#07080f";
    fallbackPainted = true;
    canvas.style.visibility = "hidden";
    opts.onError?.(
      "WebGL context lost (too many live contexts, or a GPU reset) — painted fallbackCss"
    );
  };

  // three.js re-initialises its own GL state on this event before our handler
  // runs, so resuming is just: re-apply what we own, unhide, restart the loop.
  const onContextRestored = () => {
    if (disposed) return;
    contextLost = false;
    if (fallbackPainted) {
      parent.style.background = "";
      fallbackPainted = false;
    }
    canvas.style.visibility = "";
    renderer.setClearColor(opts.clearColor ?? 0x07080f, 1);
    resize();
    last = performance.now();
    tick();
  };

  canvas.addEventListener("webglcontextlost", onContextLost);
  canvas.addEventListener("webglcontextrestored", onContextRestored);

  return {
    backend: "three",
    canvas,
    setPaused: (p: boolean) => {
      paused = !!p;
      last = performance.now();
    },
    setUniform: (name: string, value: ShaderQuadUniformValue) => {
      const u = uniforms[name];
      if (u) assignUniform(u, value);
    },
    setSpeed: (n: number) => {
      speed = Number.isFinite(n) ? n : 1;
    },
    dispose: () => {
      if (disposed) return; // idempotent: the context budget must not go negative
      disposed = true;
      liveShaderQuads = Math.max(0, liveShaderQuads - 1);
      cancelAnimationFrame(raf);
      ro.disconnect();
      io?.disconnect();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      canvas.removeEventListener("webglcontextrestored", onContextRestored);
      if (fallbackPainted) parent.style.background = "";
      material.dispose();
      quad.geometry.dispose();
      // Textures the caller handed us to own. Same tier as material/geometry:
      // three's renderer.dispose() does not free them, and a wallpaper the user
      // flips through leaks a 2 MB GPU allocation per switch without this.
      disposeOwned(opts.ownedTextures);
      renderer.dispose();
      // three's dispose() releases its own objects but leaves the GL context to
      // the GC — too slow when tiles mount and unmount against a hard cap.
      try {
        renderer.forceContextLoss();
      } catch {
        /* older/soft backends may not expose it */
      }
      canvas.remove();
    },
  };
}

function resolveBackend(): "three" | "canvas2d" {
  try {
    const v = localStorage.getItem("arclog.gfx.backend");
    if (v === "canvas2d" || v === "three") return v;
  } catch {
    /* */
  }
  return "three";
}

export function getSurface(id: string): GfxSurface | null {
  return surfaces.get(id) ?? null;
}

export function disposeSurface(id: string) {
  surfaces.get(id)?.dispose();
}

export function getThree(): typeof THREE | null {
  return resolveBackend() === "three" ? THREE : null;
}

/** @deprecated use createGraphicsApi().backend */
export function createSurface(
  parent: HTMLElement,
  opts?: { id?: string; alpha?: boolean; autoRender?: boolean }
): GfxSurface {
  return createGraphicsApi().createSurface(parent, opts);
}

export function createGraphicsApi(): GraphicsApi {
  const backend = resolveBackend();
  return {
    backend,
    three: backend === "three" ? THREE : null,
    glsl: GLSL_CHUNKS,
    createSurface: (parent, opts) =>
      backend === "canvas2d"
        ? createCanvas2dSurface(parent, opts)
        : createThreeSurface(parent, opts),
    createShaderQuad: (parent, opts) => createShaderQuadImpl(parent, opts, backend),
    // Backend-independent: it is canvas2d by definition, so a "canvas2d" OS
    // backend changes nothing for it.
    create2D: createSurface2D,
    // Pure data in, files out — no renderer or GL context involved.
    exportMesh,
    loadTexture: loadTextureImpl,
    getSurface,
    disposeSurface,
  };
}

// Back-compat alias used by older imports
export { createGraphicsApi as createCompositorApi };
